import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { InfoIcon } from "@/designSystemIcons";
import { convertSBCPrice } from "@/functions/convertPlayerPrice";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { useMediaQuery } from "usehooks-ts";

type ClubStats = {
  clubValue: number;
  potentialCoins: number;
  transferList: number;
  SBCFodder: number;
  clubRank: number;
  coins: number;
};

const BarItem = ({
  value,
  label,
  type,
  tooltipContent,
  isMainItem = false,
}: {
  value: number;
  label: string;
  type: "coin" | "rank";
  tooltipContent?: React.ReactNode;
  isMainItem?: boolean;
}) => {
  const labelSection = (
    <div className="flex flex-row gap-x-1 text-xs text-dark-tertiary items-center cursor-default">
      <p>{label}</p>
      {tooltipContent && <InfoIcon width="12" height="12" />}
    </div>
  );
  return (
    <article
      className={cn(
        "flex flex-1 flex-col gap-y-3 items-center justify-center",
        isMainItem && "bg-panel-background rounded-l-lg h-25",
      )}
    >
      <div className="flex flex-row gap-x-1 items-center justify-center">
        <p className="text-sm">{value?.toLocaleString()}</p>
        {type === "coin" && (
          <img
            className="w-3 h-3 touch-device:w-4 touch-device:h-4"
            src={`${
              import.meta.env.VITE_APP_ASSETS_BASE_URL
            }/fc24/misc/fc24coin.png`}
            alt="ut-coin"
          />
        )}
      </div>
      {tooltipContent ? (
        <Tooltip delayDuration={50} content={tooltipContent} side="bottom">
          {labelSection}
        </Tooltip>
      ) : (
        labelSection
      )}
    </article>
  );
};

const MobileBarCard = ({
  label1,
  value1,
  label2,
  value2,
}: {
  label1: string;
  value1: number;
  label2: string;
  value2: number;
}) => (
  <article className="bg-panel-background backdrop-blur-sm rounded-lg w-full shadow-soft-dark-bottom px-2 py-2 flex flex-col gap-y-2">
    <div className="flex flex-row justify-between">
      <p className="text-dark-tertiary">{label1}</p>
      <div className="flex flex-row gap-x-1 items-center">
        <p>{convertSBCPrice(value1)}</p>
        <img
          className="w-3 h-3"
          src={`${
            import.meta.env.VITE_APP_ASSETS_BASE_URL
          }/fc24/misc/fc24coin.png`}
          alt="ut-coin"
        />
      </div>
    </div>
    <div className="flex flex-row justify-between">
      <p className="text-dark-tertiary">{label2}</p>
      <div className="flex flex-row gap-x-1 items-center">
        <p>{convertSBCPrice(value2)}</p>
        <img
          className="w-3 h-3"
          src={`${
            import.meta.env.VITE_APP_ASSETS_BASE_URL
          }/fc24/misc/fc24coin.png`}
          alt="ut-coin"
        />
      </div>
    </div>
  </article>
);

export const MyClubBar = ({
  coins,
  clubValue,
  potentialCoins,
  transferList,
  SBCFodder,
  clubRank,
  clubName,
  clubImageUrl,
}: ClubStats & {
  clubName?: string;
  clubImageUrl?: string;
}) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  return isMobile ? (
    <section className="flex flex-col gap-y-2 items-center justify-center h-38 w-full px-4">
      {clubName && (
        <div className="flex flex-row justify-between w-full">
          <div className="flex flex-row gap-x-1 mr-auto">
            <img className="w-5 h-5" src={clubImageUrl} alt="ut-coin" />
            <p className="text-sm font-semibold" title={clubName}>
              {clubName}
            </p>
          </div>
          <div className="ml-auto flex flex-row gap-x-1 items-center text-sm font-semibold">
            <p>{coins?.toLocaleString()}</p>
            <img
              className="w-3 h-3"
              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc24/misc/fc24coin.png`}
              alt="ut-coin"
            />
          </div>
        </div>
      )}
      <section className="flex flex-row gap-x-2 w-full text-xs">
        <MobileBarCard
          label1="Club Value"
          value1={clubValue}
          label2="Potential Coins"
          value2={potentialCoins}
        />
        <MobileBarCard
          label1="SBC Fodder"
          value1={SBCFodder}
          label2="Transfer List"
          value2={transferList}
        />
      </section>
    </section>
  ) : (
    <section className="flex flex-row gap-x-8 items-center">
      {clubImageUrl && clubName ? (
        <div className="flex flex-col items-center gap-y-1 text-center">
          <p
            className="touch-device:text-lg font-semibold w-36 truncate"
            title={clubName}
          >
            {clubName}
          </p>
          <img
            src={clubImageUrl}
            alt={clubName}
            className="w-12 h-12 touch-device:w-16 touch-device:h-16"
          />
        </div>
      ) : (
        <div className="w-36" />
      )}
      <section className="relative flex flex-row justify-evenly items-center w-full z-10 text-sm bg-panel-middleground backdrop-blur-sm py-4 rounded-xl h-25 shadow-soft-dark-bottom">
        <BarItem value={coins} label="Coins" type="coin" isMainItem />
        <BarItem
          value={clubValue}
          label="Club Value"
          type="coin"
          tooltipContent={
            <div className="flex flex-col gap-y-1 desktop:gap-y-2 w-64 p-1 text-xs">
              <div className="font-semibold">Total value of your club</div>
              <div className="text-dark-tertiary">
                Your coins, plus the current value of all your untradeable and
                tradeable players.
              </div>
            </div>
          }
        />
        <BarItem
          value={potentialCoins}
          label="Potential Coins"
          type="coin"
          tooltipContent={
            <div className="flex flex-col gap-y-1 desktop:gap-y-2 w-64 p-1 text-xs">
              <div className="font-semibold">Maximum trading budget</div>
              <div className="text-dark-tertiary">
                Your coins, plus the current value of all your tradeable
                players.
              </div>
            </div>
          }
        />
        <BarItem
          value={SBCFodder}
          label="SBC Fodder"
          type="coin"
          tooltipContent={
            <div className="flex flex-col gap-y-1 desktop:gap-y-2 w-64 p-1 text-xs">
              <div className="font-semibold">Total fodder value for SBCs</div>
              <div className="text-dark-tertiary">
                Combined value of your untradeable players rated above 81 and
                not in your Active Squad. The SBC value of a player is the price
                of the cheapest player on the market with that rating.
              </div>
              <div className="text-dark-tertiary italic">
                For example, an 83-rated Marcos Llorente costs 20k. However, the
                cheapest 83-rated player, Morata, is worth 3k. In this case,
                your untradeable Marcos Llorente only increases your SBC fodder
                value by 3k.
              </div>
            </div>
          }
        />
        <BarItem value={transferList} label="Transfer List" type="coin" />
        <BarItem
          value={clubRank}
          label="Club Rank"
          type="rank"
          tooltipContent={
            <div className="flex flex-col gap-y-1 desktop:gap-y-2 w-64 p-1 text-xs">
              <div className="font-semibold">Wins compared to other users</div>
              <div className="text-dark-tertiary">
                Your total number of wins in-game compared to all other EasySBC
                users. Can you reach #1?
              </div>
            </div>
          }
        />
      </section>
    </section>
  );
};
